import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Globe, Instagram, Loader2, PlugZap } from "lucide-react";
import type { AccountDTO, ConnectWordpressInput } from "@app/shared";
import { api, instagramStartUrl } from "../lib/api";
import { formatDateTime } from "../lib/format";
import { Button, buttonVariants } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { useToast } from "./Toast";

/** Per-brand Instagram connection (status + connect/reconnect). */
export function IgConnect({ account }: { account: AccountDTO }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-slate-200 bg-slate-50 p-4">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-navy/5 text-navy">
          <Instagram className="h-5 w-5" />
        </div>
        <div>
          <p className="text-sm font-medium text-slate-900">Instagram</p>
          <p className="text-xs text-slate-500">
            {account.connected ? (
              <>
                Terhubung
                {account.igUserId ? ` · IG ${account.igUserId}` : ""}
                {account.igTokenExpiresAt
                  ? ` · kedaluwarsa ${formatDateTime(account.igTokenExpiresAt)}`
                  : ""}
              </>
            ) : (
              "Belum terhubung"
            )}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <Badge variant={account.connected ? "green" : "coral"}>
          {account.connected ? "Connected" : "Disconnected"}
        </Badge>
        {/* Full-page navigation so the browser follows the OAuth 302 chain. */}
        <a
          href={instagramStartUrl(account.key)}
          className={buttonVariants({ variant: "primary" })}
        >
          <PlugZap className="h-4 w-4" />
          {account.connected ? "Reconnect" : "Connect"}
        </a>
      </div>
    </div>
  );
}

/** Per-brand WordPress connection (collapsible Application-Password form). */
export function WpConnect({ account }: { account: AccountDTO }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [siteUrl, setSiteUrl] = useState(account.wpSiteUrl ?? "");
  const [username, setUsername] = useState("");
  const [appPassword, setAppPassword] = useState("");

  const connect = useMutation({
    mutationFn: () => {
      const input: ConnectWordpressInput = {
        siteUrl: siteUrl.trim(),
        username: username.trim(),
        appPassword,
      };
      return api.connectWordpress(account.key, input);
    },
    onSuccess: (updated) => {
      setUsername("");
      setAppPassword("");
      setSiteUrl(updated.wpSiteUrl ?? "");
      void queryClient.invalidateQueries({ queryKey: ["accounts"] });
      toast.success("WordPress terhubung");
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Gagal menghubungkan"),
  });

  const canSubmit =
    siteUrl.trim().length > 0 &&
    username.trim().length > 0 &&
    appPassword.length > 0 &&
    !connect.isPending;

  return (
    <details className="rounded-md border border-slate-200 bg-slate-50">
      <summary className="flex cursor-pointer select-none items-center justify-between gap-2 px-4 py-2.5">
        <span className="flex items-center gap-2 text-sm font-medium text-navy">
          <Globe className="h-4 w-4 text-slate-400" />
          WordPress
        </span>
        <Badge variant={account.wpConnected ? "green" : "coral"}>
          {account.wpConnected ? "Connected" : "Disconnected"}
        </Badge>
      </summary>
      <div className="space-y-3 border-t border-slate-200 p-4">
        <p className="text-xs text-slate-500">
          Hubungkan dengan Application Password agar draft artikel bisa terbit.
          Kredensial dienkripsi & tidak pernah ditampilkan lagi.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="space-y-1.5 sm:col-span-3">
            <Label htmlFor={`wp-site-${account.key}`}>Site URL</Label>
            <Input
              id={`wp-site-${account.key}`}
              type="url"
              placeholder="https://example.com"
              value={siteUrl}
              onChange={(e) => setSiteUrl(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`wp-user-${account.key}`}>Username</Label>
            <Input
              id={`wp-user-${account.key}`}
              autoComplete="off"
              placeholder="admin"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor={`wp-pass-${account.key}`}>Application password</Label>
            <Input
              id={`wp-pass-${account.key}`}
              type="password"
              autoComplete="new-password"
              placeholder="xxxx xxxx xxxx xxxx"
              value={appPassword}
              onChange={(e) => setAppPassword(e.target.value)}
            />
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            variant="primary"
            onClick={() => connect.mutate()}
            disabled={!canSubmit}
          >
            {connect.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <PlugZap className="h-4 w-4" />
            )}
            {account.wpConnected ? "Reconnect" : "Connect"} WordPress
          </Button>
        </div>
      </div>
    </details>
  );
}
